import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false
        };
    }

    static getDerivedStateFromError(error) {
        // Actualizar el estado para que el siguiente render muestre la UI de fallback
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Puedes loguear el error a un servicio de reporte de errores aquí
        console.error('Error caught by boundary:', error, errorInfo);

        // Guardar errorInfo para mostrarlo
        this.setState({ errorInfo });

        // En desarrollo, mostrar más información del error
        if (process.env.NODE_ENV === 'development') {
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack
            });
        }

        // En producción, intentar reportar el error
        this.reportError(error, errorInfo);
    }

    reportError = (error, errorInfo) => {
        try {
            // Crear un objeto con información del error
            const errorReport = {
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString(),
                // Agregar información adicional que pueda ser útil
                screenSize: `${window.innerWidth}x${window.innerHeight}`,
                viewport: `${window.visualViewport?.width || 'N/A'}x${window.visualViewport?.height || 'N/A'}`,
                platform: navigator.platform,
                language: navigator.language
            };

            // En producción, podrías enviar esto a un servicio de reporte de errores
            // Por ahora, lo guardamos en localStorage para debugging
            const existingErrors = JSON.parse(localStorage.getItem('errorReports') || '[]');
            existingErrors.push(errorReport);
            localStorage.setItem('errorReports', JSON.stringify(existingErrors));

            // También podrías enviar a un endpoint de tu backend
            // fetch('/api/error-report', {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify(errorReport)
            // }).catch(console.error);

        } catch (reportError) {
            console.error('Error reporting failed:', reportError);
        }
    }

    copyErrorToClipboard = () => {
        const errorText = `Error: ${this.state.error?.message}\n\nStack: ${this.state.error?.stack}\n\nComponent Stack: ${this.state.errorInfo?.componentStack}`;

        if (navigator.clipboard) {
            navigator.clipboard.writeText(errorText).then(() => {
                alert('Error copiado al portapapeles');
            }).catch(() => {
                this.fallbackCopyTextToClipboard(errorText);
            });
        } else {
            this.fallbackCopyTextToClipboard(errorText);
        }
    }

    fallbackCopyTextToClipboard = (text) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy');
            alert('Error copiado al portapapeles');
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }

        document.body.removeChild(textArea);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center shadow-lg">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">
                            Algo salió mal
                        </h2>
                        <p className="text-gray-600 mb-4">
                            Ha ocurrido un error inesperado. Por favor, recarga la página.
                        </p>

                        {/* Mostrar información básica del error en producción */}
                        {this.state.error && (
                            <div className="mb-4 p-3 bg-red-50 rounded text-left text-sm">
                                <p className="font-semibold text-red-800 mb-2">
                                    Error: {this.state.error.message}
                                </p>

                                {/* Botón para mostrar/ocultar detalles */}
                                <button
                                    onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                                    className="text-blue-600 text-xs underline mb-2"
                                >
                                    {this.state.showDetails ? 'Ocultar detalles' : 'Ver detalles del error'}
                                </button>

                                {/* Detalles del error */}
                                {this.state.showDetails && (
                                    <div className="mt-2">
                                        <details className="mb-2">
                                            <summary className="cursor-pointer text-red-600 text-xs">Stack trace</summary>
                                            <pre className="mt-1 text-xs text-red-700 overflow-auto max-h-20 bg-red-100 p-2 rounded">
                                                {this.state.error.stack}
                                            </pre>
                                        </details>

                                        {this.state.errorInfo && (
                                            <details>
                                                <summary className="cursor-pointer text-red-600 text-xs">Component stack</summary>
                                                <pre className="mt-1 text-xs text-red-700 overflow-auto max-h-20 bg-red-100 p-2 rounded">
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                )}

                                {/* Botón para copiar error */}
                                <button
                                    onClick={this.copyErrorToClipboard}
                                    className="mt-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                                >
                                    📋 Copiar error
                                </button>
                            </div>
                        )}

                        {/* Información adicional para debugging móvil */}
                        <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
                            <p>URL: {window.location.href}</p>
                            <p>Dispositivo: {navigator.userAgent.includes('Mobile') ? 'Móvil' : 'Desktop'}</p>
                            <p>Pantalla: {window.innerWidth}x{window.innerHeight}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Recargar Página
                            </button>

                            <button
                                onClick={() => window.history.back()}
                                className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Volver Atrás
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 